import { useQuery } from '@tanstack/react-query';

import {
  individualsService,
  companiesService,
  FetchIndividualsRequest,
  useToast,
} from '@aslanpay/portal-common-lib';

import { IndividualPage } from 'app/components/UI/Table/Table.types';

const useFetchEmployeesPage = ({
  ids,
  status,
  companyId,
  friendlyId,
  payCalendarId,
  externalPayrollId,
  email,
  isTerminationDateSet,
  onUnpaidLeave,
  allowTracking,
  allowMarketing,
  query,
  sortBy,
  sort,
  page,
  size,
}: Omit<FetchIndividualsRequest, 'role'>) => {
  const toast = useToast();

  return useQuery<IndividualPage, Error>(
    [
      'fetchEmployeesPage',
      ids,
      status,
      companyId,
      friendlyId,
      payCalendarId,
      externalPayrollId,
      email,
      isTerminationDateSet,
      onUnpaidLeave,
      allowTracking,
      allowMarketing,
      query,
      sortBy,
      sort,
      page,
      size,
    ],
    async () => {
      const employees = await individualsService.fetchIndividuals({
        ids,
        status,
        role: ['EMPLOYEE'],
        companyId,
        friendlyId,
        payCalendarId,
        externalPayrollId,
        email,
        isTerminationDateSet,
        onUnpaidLeave,
        allowTracking,
        allowMarketing,
        query,
        sortBy,
        sort,
        page,
        size,
      });

      const companies = await companiesService.fetchCompanies({
        ids: employees.list.map((employee) => employee.companyId),
        page: 0,
        size: 999999,
      });

      const employeesList = employees.list.map((employee) => {
        const companyName =
          companies.list.find((company) => company.id === employee.companyId)?.name || '';

        const employeeStatus =
          employee.terminationDate && employee.status === 'ACTIVE' ? 'LEAVING' : employee.status;

        return { ...employee, companyName, status: employeeStatus };
      });

      return { ...employees, list: employeesList };
    },
    {
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: `Sorry, we failed to fetch the employees${
            companyId?.length ? ' for this company' : ''
          }. Please try again.`,
        });
      },
    }
  );
};

export default useFetchEmployeesPage;
